import React, { useState } from 'react';
import { Plus, Trash2, Wallet, Building2, TrendingUp, ArrowRightLeft, X, Edit2, PiggyBank } from 'lucide-react';
import { useConfirm } from '../lib/ConfirmDialog';

const Savings = ({ data, updateData }) => {
  const confirm = useConfirm();
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [newAccount, setNewAccount] = useState({
    name: '',
    type: 'bank',
    balance: '',
    currency: 'EUR',
    description: ''
  });
  const [transfer, setTransfer] = useState({
    fromAccount: '',
    toAccount: '',
    amount: '',
    description: ''
  });
  const [viewingAccount, setViewingAccount] = useState(null);
  const [cashTransaction, setCashTransaction] = useState({
    type: 'deposit',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0]
  });

  const savingsAccounts = data.budget?.savingsAccounts || [];
  const budgetSavings = data.budget?.savings || [];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const addAccount = () => {
    if (!newAccount.name || !newAccount.balance) return;

    const account = {
      id: Date.now().toString(),
      name: newAccount.name,
      type: newAccount.type,
      balance: parseFloat(newAccount.balance),
      currency: newAccount.currency,
      description: newAccount.description,
      createdAt: new Date().toISOString().split('T')[0]
    };

    updateData({
      budget: {
        ...data.budget,
        savingsAccounts: [...savingsAccounts, account]
      }
    });

    setNewAccount({ name: '', type: 'bank', balance: '', currency: 'EUR', description: '' });
    setShowAddAccount(false);
  };

  const closeAddAccount = () => {
    setShowAddAccount(false);
    setNewAccount({ name: '', type: 'bank', balance: '', currency: 'EUR', description: '' });
  };

  const openAddAccount = () => {
    setNewAccount({ name: '', type: 'bank', balance: '', currency: 'EUR', description: '' });
    setShowAddAccount(true);
  };

  const closeTransfer = () => {
    setShowTransfer(false);
    setTransfer({ fromAccount: '', toAccount: '', amount: '', description: '' });
  };

  const openTransfer = () => {
    setTransfer({ fromAccount: '', toAccount: '', amount: '', description: '' });
    setShowTransfer(true);
  };

  const deleteAccount = async (id) => {
    if (!(await confirm({ message: 'Are you sure you want to delete this account?', danger: true }))) return;
    updateData({
      budget: {
        ...data.budget,
        savingsAccounts: savingsAccounts.filter(acc => acc.id !== id)
      }
    });
  };

  const updateAccount = () => {
    if (!editingAccount?.name) return;

    updateData({
      budget: {
        ...data.budget,
        savingsAccounts: savingsAccounts.map(acc =>
          acc.id === editingAccount.id ? { ...acc, ...editingAccount } : acc
        )
      }
    });

    setEditingAccount(null);
  };

  const handleTransfer = () => {
    if (!transfer.fromAccount || !transfer.toAccount || !transfer.amount || transfer.fromAccount === transfer.toAccount) return;

    const amount = parseFloat(transfer.amount);
    const fromAcc = savingsAccounts.find(a => a.id === transfer.fromAccount);
    const toAcc = savingsAccounts.find(a => a.id === transfer.toAccount);

    if (!fromAcc || !toAcc || fromAcc.balance < amount) {
      alert('Insufficient balance for transfer');
      return;
    }

    const updatedAccounts = savingsAccounts.map(acc => {
      if (acc.id === transfer.fromAccount) {
        return { ...acc, balance: acc.balance - amount };
      }
      if (acc.id === transfer.toAccount) {
        return { ...acc, balance: acc.balance + amount };
      }
      return acc;
    });

    updateData({
      budget: {
        ...data.budget,
        savingsAccounts: updatedAccounts
      }
    });

    setTransfer({ fromAccount: '', toAccount: '', amount: '', description: '' });
    setShowTransfer(false);
  };

  // Get savings linked to accounts (matching by accountId or name)
  const getLinkedSavings = (accountId, accountName) => {
    return budgetSavings.filter(s => {
      // Match by accountId if set
      if (s.accountId && s.accountId === accountId) return true;
      // Fallback: match by name
      const savingName = s.name || s.source || '';
      return savingName.toLowerCase().includes(accountName.toLowerCase()) ||
             accountName.toLowerCase().includes(savingName.toLowerCase());
    });
  };

  // Calculate linked savings amount for an account
  const getLinkedSavingsTotal = (accountId, accountName) => {
    const linked = getLinkedSavings(accountId, accountName);
    return linked.reduce((sum, s) => sum + s.amount, 0);
  };

  // Calculate total savings (base balance + linked budget savings)
  const totalSavings = savingsAccounts.reduce((sum, acc) => {
    const linkedAmount = getLinkedSavingsTotal(acc.id, acc.name);
    return sum + acc.balance + linkedAmount;
  }, 0);

  const getAccountIcon = (type) => {
    switch (type) {
      case 'bank': return Building2;
      case 'investment': return TrendingUp;
      case 'cash': return Wallet;
      default: return PiggyBank;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Savings Accounts</h2>
          <p className="text-sm sm:text-base text-gray-500">Manage your bank accounts and investments</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={openTransfer}
            className="btn bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-2 !px-3 !py-2 sm:!px-4"
          >
            <ArrowRightLeft size={18} />
            <span className="hidden sm:inline">Transfer</span>
          </button>
          <button
            onClick={openAddAccount}
            className="btn bg-purple-600 text-white hover:bg-purple-700 flex items-center gap-2 !px-3 !py-2 sm:!px-4"
          >
            <Plus size={18} />
            <span className="hidden sm:inline">Add Account</span>
          </button>
        </div>
      </div>

      {/* Total Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card bg-purple-50 border-purple-200">
          <p className="text-purple-600 text-sm font-medium">Total Savings</p>
          <p className="text-2xl font-bold text-purple-700">{formatCurrency(totalSavings)}</p>
        </div>
        <div className="card bg-blue-50 border-blue-200">
          <p className="text-blue-600 text-sm font-medium">Number of Accounts</p>
          <p className="text-2xl font-bold text-blue-700">{savingsAccounts.length}</p>
        </div>
        <div className="card bg-green-50 border-green-200">
          <p className="text-green-600 text-sm font-medium">Budget Savings</p>
          <p className="text-2xl font-bold text-green-700">
            {formatCurrency(budgetSavings.reduce((sum, s) => sum + s.amount, 0))}
          </p>
        </div>
        <div className="card bg-orange-50 border-orange-200">
          <p className="text-orange-600 text-sm font-medium">Bank Accounts</p>
          <p className="text-2xl font-bold text-orange-700">
            {savingsAccounts.filter(a => a.type === 'bank').length}
          </p>
        </div>
      </div>

      {/* Accounts Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {savingsAccounts.map((account) => {
          const Icon = getAccountIcon(account.type);
          const linkedSavings = getLinkedSavings(account.id, account.name);

          return (
            <div
              key={account.id}
              onClick={() => setViewingAccount(account)}
              className="card hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`p-3 rounded-lg ${
                    account.type === 'bank' ? 'bg-blue-100 text-blue-600' :
                    account.type === 'investment' ? 'bg-green-100 text-green-600' :
                    'bg-purple-100 text-purple-600'
                  }`}>
                    <Icon size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{account.name}</h3>
                    <p className="text-sm text-gray-500 capitalize">{account.type}</p>
                  </div>
                </div>
                <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => setEditingAccount(account)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg hover:bg-gray-100"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => deleteAccount(account.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-gray-100"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {(() => {
                const linkedTotal = getLinkedSavingsTotal(account.id, account.name);
                const totalBalance = account.balance + linkedTotal;
                return (
                  <div className="mb-4">
                    <p className="text-sm text-gray-500 mb-1">Balance</p>
                    <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalBalance)}</p>
                  </div>
                );
              })()}

              {account.description && (
                <p className="text-sm text-gray-500 mb-3">{account.description}</p>
              )}
            </div>
          );
        })}
      </div>

      {/* Add Account Modal */}
      {showAddAccount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Savings Account</h3>
              <button
                onClick={closeAddAccount}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Account Name (e.g., Raiffeisen Bank)"
                className="input"
                value={newAccount.name}
                onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
              />
              <select
                className="input"
                value={newAccount.type}
                onChange={(e) => setNewAccount({ ...newAccount, type: e.target.value })}
              >
                <option value="bank">Bank Account</option>
                <option value="investment">Investment</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </select>
              <input
                type="number"
                step="0.01"
                placeholder="Initial Balance"
                className="input"
                value={newAccount.balance}
                onChange={(e) => setNewAccount({ ...newAccount, balance: e.target.value })}
              />
              <textarea
                placeholder="Description (optional)"
                className="input h-20 resize-none"
                value={newAccount.description}
                onChange={(e) => setNewAccount({ ...newAccount, description: e.target.value })}
              />
              <div className="flex gap-2">
                <button onClick={addAccount} className="btn bg-purple-600 text-white hover:bg-purple-700 flex-1">
                  Add Account
                </button>
                <button
                  onClick={closeAddAccount}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Account Modal */}
      {editingAccount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Edit Account</h3>
              <button
                onClick={() => setEditingAccount(null)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Account Name"
                className="input"
                value={editingAccount.name}
                onChange={(e) => setEditingAccount({ ...editingAccount, name: e.target.value })}
              />
              <select
                className="input"
                value={editingAccount.type}
                onChange={(e) => setEditingAccount({ ...editingAccount, type: e.target.value })}
              >
                <option value="bank">Bank Account</option>
                <option value="investment">Investment</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </select>
              <input
                type="number"
                step="0.01"
                placeholder="Balance"
                className="input"
                value={editingAccount.balance}
                onChange={(e) => setEditingAccount({ ...editingAccount, balance: parseFloat(e.target.value) || 0 })}
              />
              <textarea
                placeholder="Description"
                className="input h-20 resize-none"
                value={editingAccount.description || ''}
                onChange={(e) => setEditingAccount({ ...editingAccount, description: e.target.value })}
              />
              <div className="flex gap-2">
                <button onClick={updateAccount} className="btn bg-purple-600 text-white hover:bg-purple-700 flex-1">
                  Save Changes
                </button>
                <button
                  onClick={() => setEditingAccount(null)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transfer Modal */}
      {showTransfer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Transfer Between Accounts</h3>
              <button
                onClick={closeTransfer}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <select
                className="input"
                value={transfer.fromAccount}
                onChange={(e) => setTransfer({ ...transfer, fromAccount: e.target.value })}
              >
                <option value="">From Account</option>
                {savingsAccounts.map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {acc.name} ({formatCurrency(acc.balance)})
                  </option>
                ))}
              </select>
              <select
                className="input"
                value={transfer.toAccount}
                onChange={(e) => setTransfer({ ...transfer, toAccount: e.target.value })}
              >
                <option value="">To Account</option>
                {savingsAccounts.map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {acc.name} ({formatCurrency(acc.balance)})
                  </option>
                ))}
              </select>
              <input
                type="number"
                step="0.01"
                placeholder="Amount"
                className="input"
                value={transfer.amount}
                onChange={(e) => setTransfer({ ...transfer, amount: e.target.value })}
              />
              <input
                type="text"
                placeholder="Description (optional)"
                className="input"
                value={transfer.description}
                onChange={(e) => setTransfer({ ...transfer, description: e.target.value })}
              />
              <div className="flex gap-2">
                <button onClick={handleTransfer} className="btn bg-blue-600 text-white hover:bg-blue-700 flex-1">
                  Transfer
                </button>
                <button
                  onClick={closeTransfer}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Account Detail Modal */}
      {viewingAccount && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                {(() => {
                  const Icon = getAccountIcon(viewingAccount.type);
                  return (
                    <div className={`p-3 rounded-lg ${
                      viewingAccount.type === 'bank' ? 'bg-blue-100 text-blue-600' :
                      viewingAccount.type === 'investment' ? 'bg-green-100 text-green-600' :
                      'bg-purple-100 text-purple-600'
                    }`}>
                      <Icon size={24} />
                    </div>
                  );
                })()}
                <div>
                  <h3 className="text-xl font-bold text-gray-900">{viewingAccount.name}</h3>
                  <p className="text-sm text-gray-500 capitalize">{viewingAccount.type}</p>
                </div>
              </div>
              <button
                onClick={() => { setViewingAccount(null); setCashTransaction({ type: 'deposit', amount: '', description: '', date: new Date().toISOString().split('T')[0] }); }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            {/* Balance Summary */}
            {(() => {
              const linkedTotal = getLinkedSavingsTotal(viewingAccount.id, viewingAccount.name);
              const totalBalance = viewingAccount.balance + linkedTotal;
              return (
                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <p className="text-sm text-gray-500">Total Balance</p>
                  <p className="text-3xl font-bold text-gray-900">{formatCurrency(totalBalance)}</p>
                  <div className="flex gap-4 mt-2 text-sm">
                    <span className="text-gray-500">Base: {formatCurrency(viewingAccount.balance)}</span>
                    {linkedTotal > 0 && (
                      <span className="text-purple-600">+ {formatCurrency(linkedTotal)} from budget</span>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Cash Account Transaction Controls */}
            {viewingAccount.type === 'cash' && (
              <div className="border rounded-xl p-4 mb-6">
                <h4 className="font-semibold text-gray-800 mb-3">Add Transaction</h4>
                <div className="flex gap-3 flex-wrap">
                  <select
                    className="input flex-1 min-w-[120px]"
                    value={cashTransaction.type}
                    onChange={(e) => setCashTransaction({ ...cashTransaction, type: e.target.value })}
                  >
                    <option value="deposit">Deposit (+)</option>
                    <option value="withdrawal">Withdrawal (-)</option>
                  </select>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Amount"
                    className="input flex-1 min-w-[120px]"
                    value={cashTransaction.amount}
                    onChange={(e) => setCashTransaction({ ...cashTransaction, amount: e.target.value })}
                  />
                  <input
                    type="date"
                    className="input flex-1 min-w-[140px]"
                    value={cashTransaction.date}
                    onChange={(e) => setCashTransaction({ ...cashTransaction, date: e.target.value })}
                  />
                  <input
                    type="text"
                    placeholder="Description (optional)"
                    className="input flex-1 min-w-[150px]"
                    value={cashTransaction.description}
                    onChange={(e) => setCashTransaction({ ...cashTransaction, description: e.target.value })}
                  />
                  <button
                    onClick={() => {
                      if (!cashTransaction.amount) return;
                      const amount = parseFloat(cashTransaction.amount);
                      const change = cashTransaction.type === 'withdrawal' ? -amount : amount;
                      const newBalance = viewingAccount.balance + change;
                      
                      // Add transaction record
                      const transaction = {
                        id: Date.now().toString(),
                        type: cashTransaction.type,
                        amount: amount,
                        date: cashTransaction.date,
                        description: cashTransaction.description,
                        newBalance: newBalance
                      };
                      
                      const updatedAccounts = savingsAccounts.map(acc =>
                        acc.id === viewingAccount.id
                          ? { 
                              ...acc, 
                              balance: newBalance,
                              transactions: [...(acc.transactions || []), transaction]
                            }
                          : acc
                      );
                      
                      updateData({
                        budget: {
                          ...data.budget,
                          savingsAccounts: updatedAccounts
                        }
                      });
                      
                      setViewingAccount({ ...viewingAccount, balance: newBalance, transactions: [...(viewingAccount.transactions || []), transaction] });
                      setCashTransaction({ type: 'deposit', amount: '', description: '', date: new Date().toISOString().split('T')[0] });
                    }}
                    className="btn bg-purple-600 text-white hover:bg-purple-700"
                  >
                    Add
                  </button>
                </div>
              </div>
            )}

            {/* Transactions / Linked Savings List */}
            <div>
              <h4 className="font-semibold text-gray-800 mb-3">
                {viewingAccount.type === 'cash' ? 'Transaction History' : 'Linked Savings'}
              </h4>
              {(() => {
                const linkedSavings = getLinkedSavings(viewingAccount.id, viewingAccount.name);
                const transactions = viewingAccount.transactions || [];
                const allEntries = [
                  ...linkedSavings.map(s => ({
                    id: s.id,
                    type: 'linked',
                    date: s.date,
                    amount: s.amount,
                    description: s.name || s.source || 'Savings',
                    info: s.info
                  })),
                  ...transactions.map(t => ({
                    id: t.id,
                    type: t.type,
                    date: t.date,
                    amount: t.amount,
                    description: t.description || (t.type === 'deposit' ? 'Deposit' : 'Withdrawal'),
                    info: null
                  }))
                ].sort((a, b) => new Date(b.date) - new Date(a.date));

                if (allEntries.length === 0) {
                  return (
                    <p className="text-gray-400 text-center py-8">
                      {viewingAccount.type === 'cash' 
                        ? 'No transactions yet. Add your first deposit or withdrawal above.'
                        : 'No linked savings from budget.'}
                    </p>
                  );
                }

                return (
                  <div className="space-y-2 max-h-80 overflow-auto">
                    {allEntries.map((entry) => (
                      <div key={entry.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-800">{entry.description}</span>
                            {entry.type === 'linked' && (
                              <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded">Budget</span>
                            )}
                            {entry.type === 'deposit' && (
                              <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded">Deposit</span>
                            )}
                            {entry.type === 'withdrawal' && (
                              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">Withdrawal</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                            <span>{new Date(entry.date).toLocaleDateString('de-DE')}</span>
                            {entry.info && (
                              <span className="text-gray-400">• {entry.info}</span>
                            )}
                          </div>
                        </div>
                        <span className={`font-semibold ${
                          entry.type === 'withdrawal' ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {entry.type === 'withdrawal' ? '-' : '+'}{formatCurrency(entry.amount)}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Savings;
